// Repair store helpers — read/write progress.repairs via updateProgress.
//
// Session 19AU: keeps the repair lifecycle in one file instead of scattering
// IDB writes across components. Three operations:
//   - recordAttempt: append to attempts[], update budgetUsedRmb, set updatedAt
//   - acceptCurrent: set current = output, mark latest attempt accepted
//   - revertCurrent: clear current → view falls back to AI original

import {
  repairKey,
  canRepairItem,
  canRepairInCourse,
  PER_ITEM_REPAIR_CAP_RMB,
  PER_COURSE_REPAIR_CAP_RMB,
  type RepairAttempt,
  type RepairRecord,
  type RepairFailureKind,
} from '@examforge/shared/schemas';
import type { ResolvableKind } from './resolve-item.js';
import type { RetryDiagnostic } from '../api/repair.js';
import { updateProgress } from '../storage/idb.js';

/** Append a new attempt to the repair record (creating one if none exists).
 *  Does NOT auto-accept — caller decides via acceptCurrent. */
export async function recordRepairAttempt(args: {
  courseId: string;
  kind: ResolvableKind;
  itemId: string;
  attempt: RepairAttempt;
}): Promise<RepairRecord> {
  const { courseId, kind, itemId, attempt } = args;
  const key = repairKey(kind, itemId);
  let resultRecord!: RepairRecord;
  await updateProgress(courseId, (p) => {
    if (!p.repairs) p.repairs = {};
    const existing = p.repairs[key];
    const next: RepairRecord = existing
      ? {
          ...existing,
          attempts: [...existing.attempts, attempt],
          budgetUsedRmb: existing.budgetUsedRmb + attempt.costRmb,
          updatedAt: attempt.attemptedAt,
        }
      : {
          itemKind: kind,
          itemId,
          current: null,
          attempts: [attempt],
          budgetUsedRmb: attempt.costRmb,
          updatedAt: attempt.attemptedAt,
        };
    p.repairs[key] = next;
    resultRecord = next;
  });
  return resultRecord;
}

/** Mark the latest attempt accepted + set current = its output. */
export async function acceptRepairCurrent(args: {
  courseId: string;
  kind: ResolvableKind;
  itemId: string;
  output: unknown;
}): Promise<void> {
  const { courseId, kind, itemId, output } = args;
  const key = repairKey(kind, itemId);
  await updateProgress(courseId, (p) => {
    if (!p.repairs?.[key]) return;
    const rec = p.repairs[key];
    const updatedAttempts = [...rec.attempts];
    if (updatedAttempts.length > 0) {
      const last = updatedAttempts[updatedAttempts.length - 1]!;
      updatedAttempts[updatedAttempts.length - 1] = { ...last, acceptedByUser: true };
    }
    p.repairs[key] = {
      ...rec,
      current: output,
      attempts: updatedAttempts,
      updatedAt: new Date().toISOString(),
    };
  });
}

/** Revert: drop current, fall back to AI original. Attempts remain in
 *  history so the operator dashboard can audit. */
export async function revertRepairCurrent(args: {
  courseId: string;
  kind: ResolvableKind;
  itemId: string;
}): Promise<void> {
  const { courseId, kind, itemId } = args;
  const key = repairKey(kind, itemId);
  await updateProgress(courseId, (p) => {
    if (!p.repairs?.[key]) return;
    p.repairs[key] = {
      ...p.repairs[key],
      current: null,
      updatedAt: new Date().toISOString(),
    };
  });
}

/**
 * Client mirror of the worker's `toRetryDiagnostic` (packages/ingest/src/repair/
 * retry-hints.ts). Converts a stored attempt into a Layer-3 hint that the
 * next request injects. Mirrored here (vs imported from @examforge/ingest)
 * to keep the worker bundle out of the web build — the type contract is
 * stable enough that drift is detected at TS-compile time via the shared
 * RepairFailureKind enum.
 *
 * Returns null for attempts that don't yield actionable hints (provider
 * errors, budget-exhausted) so the caller can `.filter(Boolean)`.
 */
export function toRetryDiagnostic(prev: RepairAttempt): RetryDiagnostic | null {
  const failureKind = deriveFailureKindClient(prev);
  if (!failureKind) return null;
  const diagnostic = formatRetryHintClient(prev, failureKind);
  if (!diagnostic) return null;
  return { timestamp: prev.attemptedAt, failureKind, diagnostic };
}

function deriveFailureKindClient(prev: RepairAttempt): RepairFailureKind | undefined {
  const status = prev.result.status;
  if (status === 'schema_fail') return 'schema_violation';
  if (status === 'lint_fail') return 'lint_critical';
  if (status === 'success' && prev.guardReport && prev.guardReport.forcedReverts.length > 0) {
    return 'forced_revert';
  }
  return undefined;
}

function formatRetryHintClient(prev: RepairAttempt, failureKind: RepairFailureKind): string | null {
  const r = prev.result;
  if (failureKind === 'schema_violation' && r.status === 'schema_fail') {
    const errs = r.schemaErrors.slice(0, 3).join('; ');
    return `Previous attempt violated the schema: ${errs}. Re-emit JSON that satisfies the schema exactly — every required field, every refinement.`;
  }
  if (failureKind === 'lint_critical' && r.status === 'lint_fail') {
    const issues = r.lintIssues.slice(0, 3).join('; ');
    return `Previous attempt schema-validated but failed lint rules: ${issues}. Address these specifically while keeping the rest of the item intact.`;
  }
  if (failureKind === 'forced_revert' && prev.guardReport) {
    const fields = prev.guardReport.forcedReverts.map((rv) => rv.field).slice(0, 5).join(', ');
    return `Previous attempt tried to modify protected fields (${fields}). These fields MUST remain exactly equal to the original — only edit the target fields cited in the prompt.`;
  }
  return null;
}

/** Build the `previousAttempts` payload for a Layer-3 retry from a stored
 *  RepairRecord. Returns the most recent N actionable diagnostics (3 max). */
export function buildPreviousAttemptsHints(record: RepairRecord | undefined, max = 3): RetryDiagnostic[] {
  if (!record) return [];
  const out: RetryDiagnostic[] = [];
  // Walk newest-first so the most recent failure is first if we cap.
  for (let i = record.attempts.length - 1; i >= 0 && out.length < max; i--) {
    const d = toRetryDiagnostic(record.attempts[i]!);
    if (d) out.push(d);
  }
  return out;
}

/** Pre-flight budget check — call before initiating an LLM repair request.
 *  Returns null when allowed, or a typed reason when blocked. */
export type RepairBlockedReason =
  | { code: 'item_cap'; usedUsd: number; capUsd: number }
  | { code: 'course_cap'; usedUsd: number; capUsd: number };

export function checkRepairBudget(args: {
  kind: ResolvableKind;
  itemId: string;
  repairs: Record<string, RepairRecord> | undefined;
}): RepairBlockedReason | null {
  const repairs = args.repairs ?? {};
  const itemRec = repairs[repairKey(args.kind, args.itemId)];
  if (!canRepairItem(itemRec)) {
    return {
      code: 'item_cap',
      usedUsd: itemRec?.budgetUsedRmb ?? 0,
      capUsd: PER_ITEM_REPAIR_CAP_RMB,
    };
  }
  if (!canRepairInCourse(repairs)) {
    let total = 0;
    for (const r of Object.values(repairs)) total += r.budgetUsedRmb;
    return {
      code: 'course_cap',
      usedUsd: total,
      capUsd: PER_COURSE_REPAIR_CAP_RMB,
    };
  }
  return null;
}
